import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getProfileStats, getTeamMembers, getMockUser } from '../utils/api';

function formatMoney(v) {
  if (!v && v !== 0) return null;
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}
function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}
function daysDiff(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((new Date(dateStr + 'T00:00:00') - today) / 86400000);
}
function relTime(dateStr) {
  if (!dateStr) return 'Never';
  const days = Math.round((Date.now() - new Date(dateStr)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days/30)}mo ago`;
  return `${Math.round(days/365)}y ago`;
}

const CAT_META = {
  lp_cogp:         { label: 'LP/Co-GP', cls: 'badge-green',  av: 'avatar-lp_cogp' },
  lender:          { label: 'Lender',   cls: 'badge-blue',   av: 'avatar-lender' },
  broker:          { label: 'Broker',   cls: 'badge-purple', av: 'avatar-broker' },
  partner_service: { label: 'Partner',  cls: 'badge-amber',  av: 'avatar-partner_service' },
  seller_operator: { label: 'Seller/Op', cls: 'badge-teal',  av: 'avatar-seller_operator' },
};
const DEAL_STATUS_META = {
  active:         { label: 'Active',         cls: 'badge-green' },
  under_contract: { label: 'Under Contract', cls: 'badge-amber' },
  closed:         { label: 'Closed',         cls: 'badge-neutral' },
  dead:           { label: 'Dead',           cls: 'badge-red' },
};
const CAT_TABS = [
  { key: 'all', label: 'All' },
  { key: 'lp_cogp', label: 'LP/Co-GP' },
  { key: 'lender', label: 'Lender' },
  { key: 'broker', label: 'Broker' },
  { key: 'partner_service', label: 'Partner' },
  { key: 'seller_operator', label: 'Seller/Op' },
];

const PERIODS = ['week','month','qtr','year'];
const PERIOD_LABELS = { week:'Week', month:'Month', qtr:'Quarter', year:'Year' };

function ActivityChart({ trend, period, onPeriod }) {
  if (!trend || trend.length === 0) {
    return (
      <div style={{ height:120, display:'flex', alignItems:'center', justifyContent:'center',
        color:'var(--color-text-muted)', fontSize:'0.8rem' }}>
        No activity data yet
      </div>
    );
  }
  let running = 0;
  const chartData = trend.map(row => {
    running += parseInt(row.count) || 0;
    const d = new Date(row.week);
    return {
      label: d.toLocaleDateString('en-US', { month:'short', day:'numeric' }),
      value: running,
    };
  });
  const windowMap = { week:1, month:4, qtr:13, year:52 };
  const sliced = chartData.slice(-Math.min(chartData.length, windowMap[period] || 13));
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:'0.72rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--color-text-muted)' }}>
          Cumulative interactions
        </div>
        <div style={{ display:'flex', gap:3 }}>
          {PERIODS.map(p => (
            <button key={p} onClick={() => onPeriod(p)} style={{
              padding:'2px 8px', borderRadius:4, border:'1px solid var(--color-border)',
              background: period === p ? 'var(--color-btn)' : 'transparent',
              color: period === p ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              cursor:'pointer', fontSize:'0.7rem', fontFamily:'var(--font-ui)',
            }}>{PERIOD_LABELS[p]}</button>
          ))}
        </div>
      </div>
      <ResponsiveContainer key={`activity-${period}`} width="100%" height={120}>
      <AreaChart data={sliced}
        margin={{ top:4, right:4, left:-20, bottom:0 }}>
        <defs>
          <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#6aad7b" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#6aad7b" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize:10 }}
          axisLine={false} tickLine={false}
          interval={Math.max(0, Math.floor(sliced.length / 4) - 1)} />
        <YAxis tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v) => [v, 'Interactions']} />
        <Area type="monotone" dataKey="value" stroke="#6aad7b" strokeWidth={2}
          fill="url(#actGrad)" dot={false} activeDot={{ r:4 }} />
      </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MyProfilePage({ meData }) {
  const [data, setData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [profileUserId, setProfileUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catTab, setCatTab] = useState('all');
  const [chartPeriod, setChartPeriod] = useState('qtr');

  // can_view_all_profiles: true for admin and full, false for regular and view
  // When false the profile switcher is hidden and profileUserId is locked to self
  const canViewAllProfiles = meData?.permissions?.can_view_all_profiles ?? true;

  useEffect(() => {
    getTeamMembers().then(r => {
      const members = r.data || r;
      setTeamMembers(members);
      // Use meData.id if available (real auth), fall back to mock email
      if (meData?.id) {
        setProfileUserId(meData.id);
      } else {
        const email = getMockUser();
        const me = members.find(m => m.email === email);
        if (me) setProfileUserId(me.id);
      }
    }).catch(e => setError(e.message));
  }, [meData]);

  const load = useCallback(() => {
    if (!profileUserId) return;
    setLoading(true);
    getProfileStats(profileUserId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [profileUserId]);

  useEffect(() => { load(); }, [load]);

  if (!profileUserId) return <div className="page-loading">Loading…</div>;
  if (loading) return <div className="page-loading">Loading profile…</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!data) return null;

  const { metrics, contacts, overdue_follow_ups, going_cold, my_deals, upcoming_meetings, activity_trend } = data;
  const profileMember = teamMembers.find(m => m.id === profileUserId);
  const filteredContacts = catTab === 'all' ? contacts : contacts.filter(c => c.category === catTab);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">{profileMember?.display_name || 'My Profile'}</h1>
          {profileMember && (
            <p className="page-subtitle" style={{ textTransform: 'capitalize' }}>
              {profileMember.role} · {profileMember.email}
            </p>
          )}
        </div>
        {canViewAllProfiles && teamMembers.length > 0 && (
          <div>
            <label className="form-label-sm" style={{ display: 'block', marginBottom: 4 }}>Viewing profile:</label>
            <select className="input-sm" value={profileUserId}
              onChange={e => setProfileUserId(e.target.value)} style={{ minWidth: 160 }}>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>{m.display_name} ({m.role})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Metric cards */}
      <div className="metric-row">
        <div className="metric-card">
          <div className="metric-label">My Contacts</div>
          <div className="metric-value">{parseInt(metrics.my_contacts) || 0}</div>
          <div className="metric-sub">primary + secondary</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">This Week</div>
          <div className="metric-value">{parseInt(metrics.interactions_this_week) || 0}</div>
          <div className="metric-sub">{parseInt(metrics.interactions_this_month) || 0} this month</div>
        </div>
        <div className={`metric-card ${parseInt(metrics.overdue_follow_ups) > 0 ? 'metric-danger' : ''}`}>
          <div className="metric-label">Overdue Follow-ups</div>
          <div className="metric-value" style={{ color: parseInt(metrics.overdue_follow_ups) > 0 ? 'var(--color-danger)' : undefined }}>
            {parseInt(metrics.overdue_follow_ups) || 0}
          </div>
        </div>
        <div className={`metric-card ${going_cold.length > 0 ? 'metric-warn' : ''}`}>
          <div className="metric-label">Going Cold 30d</div>
          <div className="metric-value" style={{ color: going_cold.length > 0 ? 'var(--color-warning)' : undefined }}>
            {going_cold.length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Closed Deals</div>
          <div className="metric-value">{parseInt(metrics.closed_deals) || 0}</div>
          {metrics.closed_deal_value && (
            <div className="metric-sub" style={{ color: 'var(--color-success)' }}>
              {formatMoney(metrics.closed_deal_value)}
            </div>
          )}
        </div>
        <div className="metric-card">
          <div className="metric-label">Active Deals</div>
          <div className="metric-value">{parseInt(metrics.active_deals) || 0}</div>
        </div>
      </div>

      {/* Activity chart */}
      <div className="card" style={{ marginBottom: 24, padding: '14px 16px' }}>
        <ActivityChart
          trend={activity_trend}
          period={chartPeriod}
          onPeriod={setChartPeriod}
        />
      </div>

      {/* Upcoming event meetings */}
      {upcoming_meetings.length > 0 && (
        <div className="detail-section">
          <div className="detail-section-label">Upcoming Event Meetings ({upcoming_meetings.length})</div>
          <div className="card">
            {upcoming_meetings.map((m, i) => (
              <React.Fragment key={`${m.event_id}-${m.contact_id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px' }}>
                  <div style={{ flex: 1 }}>
                    <Link to={`/contacts/${m.contact_id}`} style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--color-text-primary)', textDecoration: 'none' }}>
                      {m.contact_name}
                    </Link>
                    {m.organization && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{m.organization}</div>}
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                      <Link to={`/events/${m.event_id}`} style={{ color: 'var(--color-link)', textDecoration: 'none' }}>{m.event_name}</Link>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-text-primary)', fontWeight: 500 }}>
                      {formatDateShort(m.meeting_date)} · {formatTime(m.meeting_time)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {m.meeting_duration}m{m.meeting_location ? ` · ${m.meeting_location}` : ''}
                    </div>
                  </div>
                </div>
                {i < upcoming_meetings.length - 1 && <div className="row-divider" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* My deals */}
      {my_deals.length > 0 && (
        <div className="detail-section">
          <div className="detail-section-label">My Deals ({my_deals.length})</div>
          <div className="card">
            {my_deals.map((deal, i) => {
              const sm = DEAL_STATUS_META[deal.status] || { label: deal.status, cls: 'badge-neutral' };
              const val = deal.purchase_price || deal.underwriting_price;
              return (
                <React.Fragment key={deal.id}>
                  <Link to={`/deals/${deal.id}`} className="deal-row">
                    <div className="deal-row-main">
                      <div className="deal-row-name">{deal.name}</div>
                      <div className="deal-row-address">{deal.address}</div>
                    </div>
                    <div className="deal-row-right">
                      <span className={`badge ${sm.cls}`}>{sm.label}</span>
                      {val && <div className="deal-row-value">{formatMoney(val)}</div>}
                      {deal.close_date && <div className="deal-row-date">{formatDateShort(deal.close_date)}</div>}
                    </div>
                  </Link>
                  {i < my_deals.length - 1 && <div className="row-divider" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Overdue + Going cold panels */}
      {(overdue_follow_ups.length > 0 || going_cold.length > 0) && (
        <div className="dashboard-grid" style={{ marginBottom: 24 }}>
          {overdue_follow_ups.length > 0 && (
            <div className="dashboard-panel">
              <div className="panel-header">
                <span>Overdue Follow-ups</span>
                <span style={{ color: 'var(--color-danger)' }}>{overdue_follow_ups.length}</span>
              </div>
              {overdue_follow_ups.map(c => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="follow-up-row">
                  <div className={`contact-avatar avatar-${c.category}`} style={{ flexShrink: 0, width: 32, height: 32, fontSize: '0.7rem' }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.organization || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-danger)', fontWeight: 600 }}>
                      {Math.abs(daysDiff(c.next_follow_up))}d overdue
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>Due {formatDateShort(c.next_follow_up)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {going_cold.length > 0 && (
            <div className="dashboard-panel">
              <div className="panel-header">
                <span>Going Cold</span>
                <span style={{ color: 'var(--color-warning)' }}>{going_cold.length}</span>
              </div>
              {going_cold.map(c => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="follow-up-row">
                  <div className={`contact-avatar avatar-${c.category}`} style={{ flexShrink: 0, width: 32, height: 32, fontSize: '0.7rem' }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.organization || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-warning)' }}>
                      {c.last_interaction_date ? relTime(c.last_interaction_date) : 'Never contacted'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My contacts with category tabs */}
      <div className="detail-section">
        <div className="detail-section-label" style={{ marginBottom: 12 }}>My Contacts ({contacts.length})</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {CAT_TABS.map(tab => {
            const count = tab.key === 'all' ? contacts.length : contacts.filter(c => c.category === tab.key).length;
            if (count === 0 && tab.key !== 'all') return null;
            return (
              <button key={tab.key} className={`toggle-pill ${catTab === tab.key ? 'active' : ''}`}
                onClick={() => setCatTab(tab.key)} style={{ fontSize: '0.78rem' }}>
                {tab.label} <span style={{ opacity: 0.7 }}>({count})</span>
              </button>
            );
          })}
        </div>
        {filteredContacts.length === 0 ? (
          <div className="empty-state-inline">No contacts in this category.</div>
        ) : (
          <div className="card">
            {filteredContacts.map((c, i) => {
              const catMeta = CAT_META[c.category] || { label: c.category, cls: 'badge-neutral', av: '' };
              return (
                <React.Fragment key={c.id}>
                  <Link to={`/contacts/${c.id}`} className="contact-row">
                    <div className={`contact-avatar ${catMeta.av}`}>{c.first_name?.[0]}{c.last_name?.[0]}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>{c.first_name} {c.last_name}</div>
                      {c.organization && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{c.organization}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span className={`badge ${catMeta.cls}`} style={{ fontSize: '0.7rem' }}>{catMeta.label}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{c.ownership}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', minWidth: 70, textAlign: 'right' }}>
                        {c.last_interaction_date ? relTime(c.last_interaction_date) : 'Never'}
                      </span>
                    </div>
                  </Link>
                  {i < filteredContacts.length - 1 && <div className="row-divider" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
